import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { WaitlistDialog } from "./WaitlistDialog";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { ThemeToggle } from "./ThemeToggle";
import { useLanguage } from "@/contexts/LanguageContext";
import idkLogo from "@/assets/idk-logo.webp";

export function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { t } = useLanguage();
  const location = useLocation();
  const isBusinessPage = location.pathname === "/business";

  const scrollToWaitlist = (e: React.MouseEvent) => {
    e.preventDefault();
    setMobileMenuOpen(false);
    const el = document.getElementById("waitlist");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { labelKey: "nav.technology", href: "#technology" },
    { labelKey: "nav.marketplace", href: "#marketplace" },
    { labelKey: "nav.founders", href: "#founders" },
    { labelKey: "nav.business", href: "/business" },
    { labelKey: "nav.contact", href: "/contact" },
  ];

  return (
    <>
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
        className={`fixed top-0 left-0 right-0 z-50 overflow-visible transition-all duration-700 ${
          scrolled
            ? "liquid-glass !rounded-none border-x-0 border-t-0"
            : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <a href="/" className="flex items-center group">
              <img 
                src={idkLogo} 
                alt="IDK" 
                className="h-14 lg:h-16 w-auto object-contain"
                width={64}
                height={64}
              />
            </a>

            {/* Desktop Navigation (min 960px) */}
            <div className="hidden min-[960px]:flex items-center gap-4 xl:gap-6">
              {navLinks.map((link) => {
                const baseClass =
                  "text-sm text-muted-foreground hover:text-foreground transition-colors duration-300 whitespace-nowrap";
                return link.href.startsWith("/") ? (
                  <Link
                    key={link.labelKey}
                    to={link.href}
                    className={baseClass}
                  >
                    {t(link.labelKey)}
                  </Link>
                ) : (
                  <a
                    key={link.labelKey}
                    href={link.href}
                    className={baseClass}
                  >
                    {t(link.labelKey)}
                  </a>
                );
              })}
            </div>

            {/* Desktop CTA + Language (min 960px) */}
            <div className="hidden min-[960px]:flex items-center gap-2 xl:gap-3">
              <ThemeToggle />
              <LanguageSwitcher />
              {isBusinessPage ? (
                <button onClick={scrollToWaitlist} className="btn-nav whitespace-nowrap">
                  {t('business.nav.requestAccess')}
                </button>
              ) : (
                <WaitlistDialog>
                  <button className="btn-nav whitespace-nowrap">{t('nav.joinWaitlist')}</button>
                </WaitlistDialog>
              )}
            </div>

            {/* Mobile/Tablet Menu Button (<960px) */}
            <div className="min-[960px]:hidden flex items-center gap-2">
              <ThemeToggle />
              <LanguageSwitcher />
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="text-foreground p-2 liquid-glass !rounded-xl"
                aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
              >
                {mobileMenuOpen ? (
                  <X className="w-5 h-5" />
                ) : (
                  <Menu className="w-5 h-5" />
                )}
              </button>
            </div>
          </div>
        </div>
      </motion.nav>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 min-[960px]:hidden"
            style={{ 
              background: 'hsl(0 0% 2% / 0.95)',
              backdropFilter: 'blur(40px)',
            }}
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              transition={{ delay: 0.1 }}
              className="flex flex-col items-center justify-center h-full gap-8"
            >
              {navLinks.map((link, i) => 
                link.href.startsWith("/") ? (
                  <motion.div
                    key={link.labelKey}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 + i * 0.05 }}
                  >
                    <Link
                      to={link.href}
                      onClick={() => setMobileMenuOpen(false)}
                      className="text-3xl font-semibold text-white hover:text-gradient-rainbow transition-all duration-300"
                    >
                      {t(link.labelKey)}
                    </Link>
                  </motion.div>
                ) : (
                  <motion.a
                    key={link.labelKey}
                    href={link.href}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 + i * 0.05 }}
                    onClick={() => setMobileMenuOpen(false)}
                    className="text-3xl font-semibold text-white hover:text-gradient-rainbow transition-all duration-300"
                  >
                    {t(link.labelKey)}
                  </motion.a>
                )
              )}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.25 }}
              >
                {isBusinessPage ? (
                  <button
                    onClick={scrollToWaitlist}
                    className="btn-primary text-lg px-8 py-4 mt-4"
                  >
                    {t('business.nav.requestAccess')}
                  </button>
                ) : (
                  <WaitlistDialog>
                    <button
                      onClick={() => setMobileMenuOpen(false)}
                      className="btn-primary text-lg px-8 py-4 mt-4"
                    >
                      {t('nav.joinWaitlist')}
                    </button>
                  </WaitlistDialog>
                )}
              </motion.div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}